import type { BuiltInTool, JsonSchema } from './types'

const id: JsonSchema = { type: 'string', description: 'The task id from os.list_task_definitions.' }
const key: JsonSchema = {
    type: 'string',
    description: 'One enterprise context key, at most 512 characters, without whitespace.',
}

/** Administrative operations use the API's existing controls, never the run dispatcher. */
export const OS_TASK_ACTIVATION_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.set_task_run_status',
        family: 'os.org',
        summary: 'Enable or disable a task.',
        description:
            'Set runStatus to active or disabled. Enabling permits future starts; it does not start a run. Disabling prevents future starts without cancelling work already running. Built-in task controls remain protected by the API.',
        inputSchema: {
            type: 'object',
            properties: {
                id,
                runStatus: { type: 'string', enum: ['active', 'disabled'], description: 'Whether the task may start.' },
            },
            required: ['id', 'runStatus'],
            additionalProperties: false,
        },
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        errors: [],
        api: { method: 'PATCH', path: '/v1/tasks/:id/run-status', scope: 'tasks:write' },
        public: true,
    },
    {
        name: 'os.set_task_schedule',
        family: 'os.org',
        summary: 'Set a task schedule.',
        description:
            'Replace a task schedule. Pass cron, optionally an IANA timezone (otherwise the enterprise timezone), enabled (defaults to true), and input matching the task fields. The API validates the schedule and refuses built-in tasks. A disabled task still cannot run. Read the schedule back with os.read_task_definition.',
        inputSchema: {
            type: 'object',
            properties: {
                id,
                cron: { type: 'string', description: 'A five-field cron expression.' },
                timezone: {
                    type: 'string',
                    description: 'An IANA timezone, or enterprise to follow the enterprise timezone.',
                },
                enabled: { type: 'boolean', description: 'Whether the schedule should fire. Defaults to true.' },
                input: { type: 'object', description: 'Inputs supplied on each scheduled run.' },
            },
            required: ['id', 'cron'],
            additionalProperties: false,
        },
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        errors: [],
        api: { method: 'PUT', path: '/v1/tasks/:id/schedule', scope: 'tasks:write' },
        public: true,
    },
    {
        name: 'os.remove_task_schedule',
        family: 'os.org',
        summary: 'Remove a task schedule.',
        description:
            'Remove a task schedule so it no longer starts work on a cadence. Does not cancel existing runs. Built-in tasks are refused by the API.',
        inputSchema: { type: 'object', properties: { id }, required: ['id'], additionalProperties: false },
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        errors: [],
        api: { method: 'DELETE', path: '/v1/tasks/:id/schedule', scope: 'tasks:write' },
        public: true,
    },
    {
        name: 'os.set_task_input_values',
        family: 'os.org',
        summary: "Set this enterprise's own values for a task's inputs.",
        description:
            "Replace this enterprise's own values for the inputs a task declares, keyed by input key; pass {} to clear them. Every run of the task takes them, however it starts: a value the start gives wins, then these, then the task version's defaults. Works on any task the enterprise runs, including a linked clone whose definition another enterprise owns; it changes no version. Each key must be an input the current version declares and each value of its declared type; a file input takes none. Built-in tasks are refused. Read them back with os.read_task_definition (inputValues, and ignoredInputValues for saved values the current version no longer takes).",
        inputSchema: {
            type: 'object',
            properties: {
                id,
                values: {
                    type: 'object',
                    description: 'Input key to value: text, a number or true/false, as the input declares.',
                },
            },
            required: ['id', 'values'],
            additionalProperties: false,
        },
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        errors: [],
        api: { method: 'PUT', path: '/v1/tasks/:id/input-values', scope: 'tasks:write' },
        public: true,
    },
    {
        name: 'os.read_context',
        family: 'os.org',
        summary: 'Read enterprise workflow context outside a run.',
        description:
            'Read one enterprise context key. Returns key, value (null when absent), and updatedAt. This is workflow bookkeeping, separate from knowledge in memory.',
        inputSchema: { type: 'object', properties: { key }, required: ['key'], additionalProperties: false },
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        api: { method: 'GET', path: '/v1/enterprise/context/:key', scope: 'enterprise:read' },
        public: true,
    },
    {
        name: 'os.write_context',
        family: 'os.org',
        summary: 'Set enterprise workflow context outside a run.',
        description:
            'Store a JSON object under one enterprise context key, at most 16 KB encoded. Last write wins. Replaces the entire value, so read it before changing selected fields. Records the authenticated caller as the writer. Does not change task permissions, versions or schedules.',
        inputSchema: {
            type: 'object',
            properties: {
                key,
                value: { type: 'object', description: 'The complete JSON object to store, at most 16 KB.' },
            },
            required: ['key', 'value'],
            additionalProperties: false,
        },
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        errors: [],
        api: { method: 'PUT', path: '/v1/enterprise/context/:key', scope: 'enterprise:write' },
        public: true,
    },
]
